// FieldVision Admin - Mark Upload Row Component
// Enkelt mark-række med drag-drop upload funktionalitet

import React, { useState, useRef } from 'react';
import { uploadMap, downloadMap, deleteMap } from '../services/api-additions';

// ============================================================================
// Types
// ============================================================================

export interface MapFile {
  mapId: number;
  marknr: string;
  cvr?: string;
  mapType: string;
  mapFormat: string;
  version: string | null;
  originalFilename: string;
  fileSize: number;
  uploadedAt: string;
}

interface MarkUploadRowProps {
  orderId: number;
  marknr: string;
  areal: number;
  cvr?: string;
  existingMaps: MapFile[];
  onUploadComplete: () => void;
  onDeleteComplete: () => void;
}

type UploadState = 'idle' | 'dragover' | 'uploading' | 'error';

// ============================================================================
// Terminal/format badges
// ============================================================================

const FORMAT_BADGES: Record<string, { label: string; emoji: string }> = {
  'john-deere': { label: 'John Deere', emoji: '🚜' },
  'fendt': { label: 'Fendt', emoji: '🚜' },
  'claas': { label: 'CLAAS', emoji: '🚜' },
  'case-ih': { label: 'Case IH', emoji: '🚜' },
  'new-holland': { label: 'New Holland', emoji: '🚜' },
  'amazone': { label: 'Amazone', emoji: '🚜' },
  'agleader': { label: 'AgLeader', emoji: '🌿' },
  'topcon': { label: 'Topcon', emoji: '🎯' },
  'cci': { label: 'CCI', emoji: '🌿' },
  'bravo': { label: 'Bravo 400', emoji: '🎯' },
  'generic': { label: 'Generisk', emoji: '📄' },
};

// ============================================================================
// Helper functions
// ============================================================================

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('da-DK', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Konverter tekniske fejlbeskeder til brugervenlige beskeder
 */
function getUserFriendlyError(err: unknown): string {
  if (!(err instanceof Error)) {
    return 'Upload fejlede';
  }

  const message = err.message.toLowerCase();

  // Netværksfejl / fil ikke tilgængelig
  if (message === 'failed to fetch' || message.includes('networkerror') || message.includes('net::err')) {
    return 'Kunne ikke læse filen. Tjek at du har adgang til filen (f.eks. OneDrive login) og prøv igen.';
  }

  // Timeout
  if (message.includes('timeout') || message.includes('timed out')) {
    return 'Upload tog for lang tid. Tjek din internetforbindelse og prøv igen.';
  }

  // Server fejl
  if (message.includes('500') || message.includes('server error')) {
    return 'Der opstod en serverfejl. Prøv igen om lidt.';
  }

  // Uautoriseret
  if (message.includes('401') || message.includes('unauthorized') || message.includes('token')) {
    return 'Din session er udløbet. Log ind igen.';
  }

  // Returnér original besked hvis den er fornuftig
  return err.message;
}

// ============================================================================
// Component
// ============================================================================

const MarkUploadRow: React.FC<MarkUploadRowProps> = ({
  orderId,
  marknr,
  areal,
  cvr,
  existingMaps,
  onUploadComplete,
  onDeleteComplete,
}) => {
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [deletingMapId, setDeletingMapId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter maps for this mark (match på CVR + marknr hvis CVR er angivet)
  const mapsForMark = existingMaps.filter((m) => {
    const marknrMatch = m.marknr === marknr || m.marknr === `Mark ${marknr}`;
    if (cvr && m.cvr) {
      return marknrMatch && m.cvr === cvr;
    }
    return marknrMatch;
  });
  const hasFiles = mapsForMark.length > 0;

  // Drag handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setUploadState('dragover');
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setUploadState('idle');
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await handleFileUpload(files[0]);
    } else {
      setUploadState('idle');
    }
  };

  // File input handler
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleFileUpload(files[0]);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload logic
  const handleFileUpload = async (file: File): Promise<void> => {
    // Validate file type (ZIP or AgLeader .agsetup)
    const filename = file.name.toLowerCase();
    if (!filename.endsWith('.zip') && !filename.endsWith('.agsetup')) {
      setErrorMessage('Kun ZIP-filer og AgLeader .agsetup-filer er tilladt');
      setUploadState('error');
      return;
    }

    setUploadState('uploading');
    setErrorMessage('');
    setUploadProgress(0);

    try {
      // Simulate progress (actual progress would need XHR)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      await uploadMap(orderId, file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Success - refresh parent
      setTimeout(() => {
        setUploadState('idle');
        setUploadProgress(0);
        onUploadComplete();
      }, 500);
    } catch (err: unknown) {
      const errorMsg = getUserFriendlyError(err);
      setErrorMessage(errorMsg);
      setUploadState('error');
    }
  };

  // Download handler
  const handleDownload = async (map: MapFile): Promise<void> => {
    try {
      await downloadMap(map.mapId, map.originalFilename);
    } catch (err: unknown) {
      const errorMsg = getUserFriendlyError(err);
      alert(errorMsg);
    }
  };

  // Delete handler
  const handleDelete = async (map: MapFile): Promise<void> => {
    if (!window.confirm(`Slet ${map.originalFilename}?`)) {
      return;
    }

    setDeletingMapId(map.mapId);
    try {
      await deleteMap(map.mapId);
      onDeleteComplete();
    } catch (err: unknown) {
      const errorMsg = getUserFriendlyError(err);
      alert(errorMsg);
    } finally {
      setDeletingMapId(null);
    }
  };

  // Click handler for drop zone
  const handleDropZoneClick = (): void => {
    fileInputRef.current?.click();
  };

  // Get background color based on state
  const getBackgroundColor = (): string => {
    if (hasFiles) return '#dcfce7'; // Green for uploaded
    if (uploadState === 'dragover') return '#dcfce7';
    if (uploadState === 'uploading') return '#fef3c7';
    if (uploadState === 'error') return '#fee2e2';
    return '#f3f4f6';
  };

  // Get border style based on state
  const getBorderStyle = (): string => {
    if (uploadState === 'dragover') return '2px dashed #16a34a';
    if (uploadState === 'error') return '1px solid #dc2626';
    return '1px solid #e5e7eb';
  };

  return (
    <div style={styles.container}>
      {/* Mark info */}
      <div style={styles.markInfo}>
        <span style={styles.markName}>{marknr}{cvr && <span style={{color: '#9ca3af', fontSize: '11px', marginLeft: '8px'}}>CVR: {cvr}</span>}</span>
        <span style={styles.markArea}>{areal.toFixed(2)} ha</span>
      </div>

      {/* Upload zone / file list */}
      <div
        style={{
          ...styles.uploadZone,
          backgroundColor: getBackgroundColor(),
          border: getBorderStyle(),
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Uploading state */}
        {uploadState === 'uploading' && (
          <div style={styles.uploadingContent}>
            <div style={styles.progressText}>Uploader... {uploadProgress}%</div>
            <div style={styles.progressBarContainer}>
              <div
                style={{
                  ...styles.progressBar,
                  width: `${uploadProgress}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Error state */}
        {uploadState === 'error' && (
          <div style={styles.errorContent}>
            <span style={styles.errorIcon}>!</span>
            <span style={styles.errorText}>{errorMessage}</span>
            <button
              onClick={() => setUploadState('idle')}
              style={styles.retryButton}
            >
              Prøv igen
            </button>
          </div>
        )}

        {/* Idle/dragover state - show files or drop zone */}
        {(uploadState === 'idle' || uploadState === 'dragover') && (
          <>
            {/* Existing files */}
            {hasFiles && (
              <div style={styles.fileList}>
                {mapsForMark.map((map) => {
                  const formatBadge = FORMAT_BADGES[map.mapFormat] || FORMAT_BADGES['generic'];
                  return (
                    <div key={map.mapId} style={styles.fileItem}>
                      <div style={styles.fileInfo}>
                        <span style={styles.formatBadge}>
                          {formatBadge.emoji} {formatBadge.label}
                        </span>
                        <span style={styles.mapType}>{map.mapType}</span>
                        <span style={styles.fileName}>{map.originalFilename}</span>
                        <span style={styles.fileMeta}>
                          {formatFileSize(map.fileSize)} | {formatDate(map.uploadedAt)}
                        </span>
                      </div>
                      <div style={styles.fileActions}>
                        <button
                          onClick={() => handleDownload(map)}
                          style={styles.actionButton}
                          title="Download"
                        >
                          Download
                        </button>
                        <button
                          onClick={() => handleDelete(map)}
                          style={styles.deleteButton}
                          disabled={deletingMapId === map.mapId}
                          title="Slet"
                        >
                          {deletingMapId === map.mapId ? '...' : 'Slet'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Drop zone / add more */}
            <div
              style={{
                ...styles.dropZone,
                ...(uploadState === 'dragover' ? styles.dropZoneActive : {}),
              }}
              onClick={handleDropZoneClick}
            >
              <span style={styles.dropIcon}>
                {uploadState === 'dragover' ? '+' : hasFiles ? '+' : ''}
              </span>
              <span style={styles.dropText}>
                {uploadState === 'dragover'
                  ? 'Slip for at uploade'
                  : hasFiles
                  ? 'Tilføj flere filer'
                  : 'Træk fil hertil eller klik for at vælge'}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,.agsetup"
        style={styles.hiddenInput}
        onChange={handleFileSelect}
      />
    </div>
  );
};

// ============================================================================
// Styles
// ============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '12px 16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  markInfo: {
    minWidth: '120px',
    flexShrink: 0,
  },
  markName: {
    display: 'block',
    fontWeight: 500,
    color: '#1f2937',
    fontSize: '14px',
  },
  markArea: {
    display: 'block',
    fontSize: '13px',
    color: '#6b7280',
  },
  uploadZone: {
    flex: 1,
    minHeight: '60px',
    borderRadius: '8px',
    padding: '12px',
    transition: 'all 0.2s ease',
  },
  uploadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  progressText: {
    fontSize: '14px',
    color: '#f59e0b',
    fontWeight: 500,
  },
  progressBarContainer: {
    width: '100%',
    height: '8px',
    backgroundColor: '#fef3c7',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#f59e0b',
    borderRadius: '4px',
    transition: 'width 0.2s ease',
  },
  errorContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  errorIcon: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#dc2626',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  errorText: {
    flex: 1,
    color: '#dc2626',
    fontSize: '14px',
  },
  retryButton: {
    padding: '6px 12px',
    fontSize: '13px',
    color: '#dc2626',
    backgroundColor: 'white',
    border: '1px solid #dc2626',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '8px',
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  formatBadge: {
    padding: '2px 8px',
    backgroundColor: '#f3f4f6',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#374151',
    whiteSpace: 'nowrap',
  },
  mapType: {
    padding: '2px 8px',
    backgroundColor: '#dbeafe',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#2563eb',
    fontWeight: 500,
  },
  fileName: {
    fontSize: '13px',
    color: '#1f2937',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileMeta: {
    fontSize: '12px',
    color: '#9ca3af',
    whiteSpace: 'nowrap',
  },
  fileActions: {
    display: 'flex',
    gap: '8px',
    marginLeft: '12px',
  },
  actionButton: {
    padding: '4px 10px',
    fontSize: '12px',
    color: '#2563eb',
    backgroundColor: 'white',
    border: '1px solid #2563eb',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '4px 10px',
    fontSize: '12px',
    color: '#dc2626',
    backgroundColor: 'white',
    border: '1px solid #dc2626',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  dropZone: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    borderRadius: '6px',
    border: '2px dashed #d1d5db',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  dropZoneActive: {
    borderColor: '#16a34a',
    backgroundColor: '#f0fdf4',
  },
  dropIcon: {
    fontSize: '16px',
    color: '#6b7280',
  },
  dropText: {
    fontSize: '13px',
    color: '#6b7280',
  },
  hiddenInput: {
    display: 'none',
  },
};

export default MarkUploadRow;
